<script lang="ts">
import { min, regex, required } from '@vee-validate/rules';
import { Field } from 'vee-validate';
import { computed, defineComponent, PropType, ref } from 'vue';
import { useStore } from 'vuex';

import FieldErrorIcon from '@/components/base/assets/FieldErrorIcon.vue';
import FloatingLabel from '@/components/base/form/FloatingLabel.vue';
import InlineError from '@/components/base/form/InlineError.vue';
import Input from '@/components/base/form/Input.vue';
import PopperWrapper from '@/components/base/layout/PopperWrapper.vue';
import ThemedButton from '@/components/base/ThemedButton.vue';
import { useDelivery } from '@/composables/useDelivery';
import { FlexibleMessageValidator, useForm } from '@/composables/useForm';
import { NutsAddress } from '@/utils/address';
import { njWarehouseAddress } from '@/utils/cart';

export default defineComponent({
  name: 'PickupInfoForm',
  props: {
    modelValue: { required: true, type: Object as PropType<NutsAddress> },
  },
  emits: ['step-complete', 'tooltip', 'update:modelValue'],
  components: {
    Field,
    FieldErrorIcon,
    FloatingLabel,
    InlineError,
    // eslint-disable-next-line vue/no-reserved-component-names
    Input,
    PopperWrapper,
    ThemedButton,
  },
  setup(props, { emit }) {
    const store = useStore();
    const { applyShippingOffers, getPickupShippingOffer, specialDeliveryShipments } =
      useDelivery(store);
    const isLoading = ref(false);

    const { errorMessages, validatorRegex, validatorFailed } = useForm();
    const schema: Record<'pickupFullName' | 'pickupPhone', FlexibleMessageValidator<string>> = {
      pickupFullName: (value) => required(value) || errorMessages.name.required,
      pickupPhone: (value) => {
        if (!required(value)) return errorMessages.phone.required;
        if (!regex(value, [validatorRegex.phone])) return errorMessages.phone.regex;
        if (!min(value, [10])) return errorMessages.phone.min.replace('{length}', '10');
        return true;
      },
    };

    const info = computed({
      get: () => props.modelValue,
      set: (val) => emit('update:modelValue', val),
    });

    const setPickup = async () => {
      isLoading.value = true;
      const { name, phone } = info.value;
      const offer = await getPickupShippingOffer({
        ...njWarehouseAddress,
        name,
        phone,
      });
      let { key } = info.value;
      if (!key) key = `shipment-${specialDeliveryShipments.value.length + 1}`;
      await applyShippingOffers([{ key, offer, addHeatResistantPacking: false }]);
      isLoading.value = false;
      emit('step-complete');
    };

    const tooltip = (source: string) => emit('tooltip', source);

    const tooltipTriggerUtilities = [
      'bg-black',
      'cursor-pointer',
      'h-3.5',
      'md:h-4',
      'rounded-full',
      'text-tiny',
      'text-white',
      'w-3.5',
      'md:w-4',
    ];

    return {
      errorMessages,
      info,
      infoIconFilled: nutshell['img/info-icon-filled.svg'],
      isLoading,
      schema,
      setPickup,
      tooltip,
      tooltipTriggerUtilities,
      validatorFailed,
    };
  },
});
</script>

<template>
  <div data-test="pickup-form">
    <div class="text-base font-normal">At Cranford office</div>
    <Field
      v-model="info.name"
      name="pickupFullName"
      :rules="schema.pickupFullName"
      v-slot="{ errors, field, meta }"
    >
      <div class="relative w-full mt-6 md:mt-8">
        <Input
          v-bind="field"
          type="text"
          id="pickup-full-name"
          placeholder="full name"
          aria-label="full name"
          :invalid="validatorFailed(meta)"
          data-test="pickup-full-name"
        />
        <FloatingLabel class="left-0" :invalid="validatorFailed(meta)" data-test="full-name-label">
          Full Name*
        </FloatingLabel>
        <FieldErrorIcon class="z-20" :showErrorIcon="validatorFailed(meta)" />
        <InlineError
          :errors="errors"
          fieldName="Full Name"
          :invalid="validatorFailed(meta)"
          data-test="pickup-full-name-validation-error"
        />
      </div>
    </Field>
    <Field
      v-model="info.phone"
      name="pickupPhone"
      :rules="schema.pickupPhone"
      v-slot="{ errors, field, meta }"
    >
      <div class="flex items-center mt-5">
        <div class="relative w-2/3 md:w-7/12">
          <Input
            v-bind="field"
            type="tel"
            id="pickup-phone"
            placeholder="pickup-phone"
            aria-label="pickup-phone"
            autocomplete="tel"
            :invalid="validatorFailed(meta)"
            data-test="pickup-phone"
          />
          <FloatingLabel
            class="left-0"
            :invalid="validatorFailed(meta)"
            data-test="pickup-phone-label"
          >
            Phone*
          </FloatingLabel>
          <FieldErrorIcon :showErrorIcon="validatorFailed(meta)" />
          <InlineError
            :errors="errors"
            fieldName="Phone"
            :invalid="validatorFailed(meta)"
            data-test="pickup-phone-validation-error"
          />
        </div>
        <div class="tiny-note popover-container">
          <PopperWrapper
            arrow
            class="legacy-max-width"
            offsetDistance="10"
            placement="bottom-start"
            @open:popper="tooltip('Phone')"
          >
            <template v-slot:content>
              <div class="p-3 text-xs text-neutral-500" data-test="pickup-phone-number-popper">
                In case we’ll need to reach out about your order
              </div>
            </template>
            <img
              role="button"
              aria-describedby="pickup-phone-number-popper-locator"
              alt="phone number information"
              class="ml-2 transform -translate-y-0.5"
              :class="tooltipTriggerUtilities"
              :src="infoIconFilled"
            />
          </PopperWrapper>
          <span id="pickup-phone-number-popper-locator" class="sr-only">
            phone number information is shown in a tooltip
          </span>
        </div>
      </div>
    </Field>
    <ThemedButton
      type="submit"
      class="mt-7"
      theme="gray"
      :isLoading="isLoading"
      data-test="pickup-submit"
      @click="setPickup"
    >
      <slot name="submitText">Continue</slot>
    </ThemedButton>
  </div>
</template>
